<script setup lang="ts">
import { FontAwesomeIcon } from '@sunlabde/m2m-library';
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import TimelineItem from './TimelineItem.vue';
import CardSegmentsWrapper from './CardSegmentsWrapper.vue';
import { getLocalizedDisplayName, getRecurringFeeInCents, type PlanVariant } from '@/models/PlanVariant';
import { useLanguage } from '@/composables/useLanguage';
import { useCartStore } from '@/store/cart';
import { getInstrumentIconBySlugOrFallback, instruments } from '@/utilities/Instrument';
import { getFormattedPrice } from '@/models/Currency';
import { planTypeDescriptions } from '@/models/PlanType';
import { useLocalization } from '@/models/Localization';
import { getPaymentPeriodDurationNote, getPrice, type PaymentPeriod } from '@/models/PaymentPeriod';

const props = withDefaults(defineProps<{
	paymentPeriods: PaymentPeriod[];
	isTrialOrder?: boolean;
	hasNoPayment?: boolean;
}>(), {
	hasNoPayment: false,
	isTrialOrder: false,
});

const { selectedLanguage } = useLanguage();
const { localize } = useLocalization();

const cartStore = useCartStore();
const { cart, themeFromSelectedPlan } = storeToRefs(cartStore);

const instrumentIds = computed(() => Object.values(instruments).map((instrument) => instrument.id));
const instrumentSlug = computed(() => instrumentIds.value.find((id) => id === themeFromSelectedPlan.value) ?? '');

const isPlus = computed(() => themeFromSelectedPlan.value === 'plus');

const planTypeNameLocalization = computed(() => {
	if (isPlus.value) {
		return planTypeDescriptions.plus.name;
	}
	return planTypeDescriptions.single.name;
});

const selectedPlanVariant = computed(() => {
	if (!cart.value) return undefined;

	return cart.value.planVariant;
});

const getDisplayName = (planVariant: PlanVariant) => getLocalizedDisplayName(planVariant, selectedLanguage.value.code);

const totalPriceInCents = computed(() => {
	if (props.hasNoPayment || !selectedPlanVariant.value) {
		return 0;
	}

	return getRecurringFeeInCents(selectedPlanVariant.value);
});

const currency = computed(() => selectedPlanVariant.value && selectedPlanVariant.value.isoCurrency ? selectedPlanVariant.value.isoCurrency : 'EUR');

const getTimelineSubtitleFromPaymentPeriod = (periods: PaymentPeriod[], index: number) => {
	const paymentPeriod = periods[index];

	if (paymentPeriod.note) {
		return localize(paymentPeriod.note);
	}

	return localize(getPaymentPeriodDurationNote(periods, index) ?? '');
};
</script>

<template>
	<CardSegmentsWrapper v-if="selectedPlanVariant">
		<template #header>
			<div class="flex justify-between items-center">
				<div class="flex gap-4">
					<FontAwesomeIcon
						class="h-5 text-white bg-secondary-500 p-4 rounded-2xl"
						:icon="getInstrumentIconBySlugOrFallback(instrumentSlug)"
					/>
					<div>
						<p class="font-serif text-lg font-bold text-secondary-500 mobile:text-xl text-center">
							{{ getDisplayName(selectedPlanVariant) }}
						</p>
						<p>{{ localize(planTypeNameLocalization) }}</p>
					</div>
				</div>
				<p class="text-base">
					{{
						getFormattedPrice(
							totalPriceInCents,
							currency,
							{
								fromCents: true,
							},
						)
					}}
				</p>
			</div>
		</template>
		<template #body>
			<div>
				<TimelineItem
					v-for="(paymentPeriod, index) in paymentPeriods"
					:key="index"
					:title="localize(paymentPeriod.label)"
					:subtitle="getTimelineSubtitleFromPaymentPeriod(paymentPeriods, index)"
					direction="vertical"
					:color="paymentPeriods.length - 1 === index && paymentPeriods.length !== 1 ? 'black' : 'green'"
				>
					<div
						v-if="!paymentPeriod.hidePrice"
						class="ml-auto flex justify-end items-start"
					>
						<template v-if="paymentPeriod.reductionPercent">
							<div class="flex gap-2 items-center flex-wrap justify-end">
								<span class="font-bold line-through whitespace-nowrap leading-none">
									{{
										getFormattedPrice(paymentPeriod.priceInCents, currency, {
											fromCents: true

										})
									}}
								</span>
								<AppBadge color="green-light">
									{{ `-${paymentPeriod.reductionPercent}%` }}
								</AppBadge>
								<span class="whitespace-nowrap leading-none">
									{{
										getFormattedPrice(getPrice(paymentPeriod), currency, {
											fromCents: true
										})
									}}
								</span>
							</div>
						</template>
						<span
							v-else
							class="whitespace-nowrap"
							:class="{ 'font-bold self-start': index === 0 }"
						>
							{{
								getFormattedPrice(paymentPeriod.priceInCents, currency, {
									fromCents: true
								})
							}}
						</span>
					</div>
				</TimelineItem>
			</div>
			<slot name="body" />
		</template>
		<template #footer>
			<slot name="footer" />
		</template>
	</CardSegmentsWrapper>
</template>
